import React, { useContext, useEffect, useMemo, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import Popover from '@mui/material/Popover';
import { Link } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import DocumentLibraryAPI, {
  DocumentLibraryEntry,
  DocumentLibraryFile,
  DocumentLibraryOrigin,
  DocumentLibraryReference,
} from 'api/DocumentLibraryAPI';
import './library.css';
import {
  IconFileText,
  IconDatabase,
  IconDownload,
  IconSearch,
  IconTable,
} from './LibraryIcons';

type FilterKey = 'all' | 'report-builder' | 'document-upload';

const LIBRARY_PAGE_SIZE = 25;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All sources' },
  { key: 'report-builder', label: 'Report Builder' },
  { key: 'document-upload', label: 'Document Upload' },
];

const originForFilter = (
  filter: FilterKey,
): DocumentLibraryOrigin | undefined =>
  filter === 'report-builder'
    ? 'report_builder'
    : filter === 'document-upload'
    ? 'document_upload'
    : undefined;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const referenceLabel = (reference: DocumentLibraryReference): string => {
  if (reference.referenceType === 'ace_run') return 'ACE';
  if (reference.referenceType === 'tariff_database') return 'Tariff';
  return 'Product Database';
};

const referencePath = (reference: DocumentLibraryReference): string => {
  if (reference.referenceType === 'ace_run') {
    return `/ace-analyzers/${reference.referenceId}`;
  }
  if (reference.referenceType === 'tariff_database') {
    return `/database/${reference.referenceId}`;
  }
  return `/import-hub/db/${reference.referenceId}`;
};

const FileDownload: React.FC<{
  file: DocumentLibraryFile;
  teamId: string;
  className: string;
}> = ({ file, teamId, className }) => {
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (file.status !== 'ready' || downloading) return;
    setDownloading(true);
    try {
      const result = await DocumentLibraryAPI.getDownload(teamId, file.id);
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.warn(`Unable to download ${file.fileName}.`, error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type='button'
      className={className}
      disabled={file.status !== 'ready' || downloading}
      onClick={handleDownload}
      title={`Download ${file.fileName}`}
    >
      {className === 'dlb-output' && <IconDownload />}
      <span>{downloading ? 'Preparing…' : file.fileName}</span>
      {className === 'dlb-source-download' && <IconDownload />}
    </button>
  );
};

const OutputFiles: React.FC<{
  files: DocumentLibraryFile[];
  teamId: string;
}> = ({ files, teamId }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (files.length === 0) return null;

  if (files.length === 1) {
    return (
      <FileDownload file={files[0]} teamId={teamId} className='dlb-output' />
    );
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <button
        type='button'
        className='dlb-output dlb-output-trigger'
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-haspopup='dialog'
        aria-expanded={open}
      >
        <IconFileText />
        <span>View output files</span>
        <span className='dlb-output-count'>{files.length}</span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: 'dlb-output-popover' }}
      >
        <div className='dlb-output-popover-title'>Output files</div>
        <div className='dlb-output-popover-list'>
          {files.map((file) => (
            <FileDownload
              key={file.id}
              file={file}
              teamId={teamId}
              className='dlb-output'
            />
          ))}
        </div>
      </Popover>
    </>
  );
};

const Library = () => {
  const { cur_user } = useContext(GlobalContext);
  const teamId = cur_user?.selected_team?.id || '';
  const [filter, setFilter] = useState<FilterKey>('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [rows, setRows] = useState<DocumentLibraryEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [teamId]);

  useEffect(() => {
    if (!teamId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError('');
    DocumentLibraryAPI.list(teamId, {
      origin: originForFilter(filter),
      query: debouncedQuery,
      page: page,
      limit: LIBRARY_PAGE_SIZE,
    })
      .then((result) => {
        if (active) {
          setRows(result.data);
          setTotal(result.total);
          const lastPage = Math.max(
            1,
            Math.ceil(result.total / LIBRARY_PAGE_SIZE),
          );
          if (page > lastPage) setPage(lastPage);
        }
      })
      .catch(() => {
        if (active) {
          setRows([]);
          setTotal(0);
          setError('Unable to load the Document Library.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [teamId, filter, debouncedQuery, page]);

  const pageCount = Math.max(1, Math.ceil(total / LIBRARY_PAGE_SIZE));

  const renderedRows = useMemo(
    () =>
      rows.map((entry) => ({
        entry: entry,
        inputFiles: entry.files.filter((file) => file.role === 'input'),
        outputFiles: entry.files.filter((file) => file.role === 'output'),
        inputReferences: entry.references.filter((reference) => reference.role === 'input'),
        outputReferences: entry.references.filter((reference) => reference.role === 'output'),
      })),
    [rows],
  );

  return (
    <div className='dlb-root'>
      <div className='dlb-header'>
        <div className='dlb-title'>Document Library</div>
        <div className='dlb-subtitle'>
          Documents from every tool — PDFs added in Document Upload and sessions
          run in Report Builder — with their source, date, and any output produced.
        </div>
      </div>

      <div className='dlb-toolbar'>
        <div className='dlb-chips'>
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type='button'
              className={`dlb-chip${filter === item.key ? ' is-active' : ''}`}
              onClick={() => {
                setFilter(item.key);
                setPage(1);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className='dlb-search'>
          <IconSearch />
          <input
            className='dlb-search-input'
            placeholder='Search by source, person, or output'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      <div className='dlb-table-wrap'>
        <table className='dlb-table'>
          <thead>
            <tr>
              <th>Source</th>
              <th>Type</th>
              <th>Created by</th>
              <th>Date</th>
              <th>Output</th>
            </tr>
          </thead>
          <tbody>
            {renderedRows.map(
              ({ entry, inputFiles, outputFiles, inputReferences, outputReferences }) => (
                <tr key={entry.id}>
                  <td>
                    <div className='dlb-source-list'>
                      {inputFiles.map((file) => (
                        <div className='dlb-source' key={file.id}>
                          <div className='dlb-source-icon is-pdf'>
                            <IconFileText />
                          </div>
                          <FileDownload
                            file={file}
                            teamId={teamId}
                            className='dlb-source-download'
                          />
                        </div>
                      ))}
                      {inputReferences.map((reference) => (
                        <div className='dlb-source' key={reference.id}>
                          <div
                            className={`dlb-source-icon ${
                              reference.referenceType === 'product_database'
                                ? 'is-dataset'
                                : 'is-run'
                            }`}
                          >
                            {reference.referenceType === 'product_database' ? (
                              <IconDatabase />
                            ) : (
                              <IconTable />
                            )}
                          </div>
                          <Link
                            className='dlb-source-name dlb-source-link'
                            to={referencePath(reference)}
                          >
                            {reference.referenceName}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className='dlb-badges'>
                      {inputFiles.map((file) => (
                        <span key={file.id} className='dlb-badge is-pdf'>
                          PDF filing
                        </span>
                      ))}
                      {inputReferences.map((reference) => (
                        <span key={reference.id} className='dlb-badge is-dataset'>
                          {referenceLabel(reference)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className='dlb-muted'>
                    {entry.createdByName || 'Unknown user'}
                  </td>
                  <td className='dlb-muted'>{formatDate(entry.createdAt)}</td>
                  <td>
                    <div className='dlb-source-list'>
                      <OutputFiles files={outputFiles} teamId={teamId} />
                      {outputReferences.map((reference) => (
                        <Link
                          key={reference.id}
                          className='dlb-output-db'
                          to={referencePath(reference)}
                        >
                          <div className='dlb-output-db-icon'>
                            <IconDatabase />
                          </div>
                          <div className='dlb-output-db-text'>
                            <div className='dlb-output-db-name'>
                              {reference.referenceName}
                            </div>
                            <div className='dlb-output-db-note'>
                              Product database created
                            </div>
                          </div>
                        </Link>
                      ))}
                      {outputFiles.length === 0 && outputReferences.length === 0 && (
                        <span className='dlb-pending'>
                          {entry.status === 'processing'
                            ? 'Processing…'
                            : entry.status === 'failed'
                            ? 'Run failed'
                            : 'No output yet'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>

        {!loading && !error && renderedRows.length === 0 && (
          <div className='dlb-empty'>
            <div className='dlb-empty-icon'>
              <IconTable />
            </div>
            <div className='dlb-empty-title'>No matching records</div>
            <div className='dlb-empty-text'>
              Try a different search term or filter.
            </div>
          </div>
        )}
        {loading && <div className='dlb-empty dlb-empty-text'>Loading library…</div>}
        {!loading && error && <div className='dlb-empty dlb-empty-text'>{error}</div>}
        {!error && total > LIBRARY_PAGE_SIZE && (
          <div className='hb-pagination dlb-pagination'>
            <span className='dlb-pagination-summary'>
              Page {page} of {pageCount} · {total} records
            </span>
            <Pagination
              page={page}
              count={pageCount}
              size='small'
              shape='rounded'
              disabled={loading}
              onChange={(_event, nextPage) => setPage(nextPage)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
